import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom-v5';
import {
  Card,
  CTAButton,
  Display2,
  Heading4,
  InfoNotification,
  Stack,
} from '@ovotech/element-v4';
import {
  AnalyticsClick,
  AnalyticsView,
  useAnalytics,
  useCustomerContext,
} from '@ovotech/ui-tools';
import { useIsTuwyCustomerQuery } from '@/src/api/use-is-tuwy-customer';
import {
  HOME_MOVES_MAKE_PAYMENT_CTA,
  HOME_MOVES_MAKE_PAYMENT_EXPERIMENT,
  HOME_MOVES_MAKE_PAYMENT_SECTION,
} from '@/src/constants/analytics-subjects';
import { MOVING_HOME_MAKE_PAYMENT } from '@/src/constants/feature-flags';
import { ROUTE_MAKE_CARD_PAYMENT } from '@/src/constants/routes';
import { BalanceType, useBalance } from '@/src/pages/Payments/hooks/useBalance';
import { formatPoundsAmount } from '@/src/utils/money';
import { useDecision } from '@/src/utils/optimizely';
import styles from './MovingHomePaymentSection.module.css';

// Only mounted for eligible customers, so ineligible ones aren't bucketed into the experiment
const MakePaymentExperiment = ({ balance }: { balance: string }) => {
  const history = useHistory();
  const { dispatch } = useAnalytics();
  const [{ enabled, variationKey }, clientReady] = useDecision(
    MOVING_HOME_MAKE_PAYMENT,
  );

  useEffect(() => {
    if (!clientReady) return;

    dispatch({
      type: 'view',
      name: HOME_MOVES_MAKE_PAYMENT_EXPERIMENT,
      properties: {
        optimizelyExperiments: { [MOVING_HOME_MAKE_PAYMENT]: variationKey },
      },
    });
  }, [clientReady, dispatch, variationKey]);

  if (!enabled) {
    return null;
  }

  return (
    <AnalyticsView name={HOME_MOVES_MAKE_PAYMENT_SECTION}>
      <div>
        <Stack spaceBetween={3}>
          <InfoNotification
            id="moving-home-make-a-payment"
            title="Make a payment"
          >
            You can make a payment towards your final bill now, to help you
            manage costs while you settle into your new home.
          </InfoNotification>

          <Card>
            <Heading4 className={styles.title}>Your balance</Heading4>
            <Display2 className={styles.amount}>
              {formatPoundsAmount(balance)}
            </Display2>
          </Card>

          <AnalyticsClick name={HOME_MOVES_MAKE_PAYMENT_CTA}>
            <CTAButton
              className={styles.button}
              onClick={() => history.push(ROUTE_MAKE_CARD_PAYMENT)}
            >
              Make a payment
            </CTAButton>
          </AnalyticsClick>
        </Stack>
      </div>
    </AnalyticsView>
  );
};

export const MovingHomePaymentSection = () => {
  const { customerId } = useCustomerContext();
  const takingOvoWithThemQuery = useIsTuwyCustomerQuery(customerId);
  const { balanceType, collectableBalance, liveBalance, isLoading, isError } =
    useBalance();

  // Mirrors CardPaymentSession so the balance shown matches the amount pre-filled there
  const balance =
    balanceType === BalanceType.CollectableBalance && collectableBalance
      ? collectableBalance
      : liveBalance;

  // A failed lookup would otherwise treat customers taking OVO with them as leavers
  const isLeavingOvo = takingOvoWithThemQuery.data?.isTuwyCustomer === false;

  if (
    !isLeavingOvo ||
    isLoading ||
    isError ||
    balance === undefined ||
    Number(balance) >= 0
  ) {
    return null;
  }

  return <MakePaymentExperiment balance={balance} />;
};
